import { getGameTemplate } from './gameTemplates';
import { getQuestionPrompt, questionRefinementPrompt } from './questionPrompts';
import anthropic from '../apiService';
import PromptService from '../promptsService';
import { parseThemeColors } from './gameTemplates';
import { DEFAULT_BACKGROUND_IMAGE_URL } from '../../../constants';

export class TemplateManager {
  constructor() {
    this.supportedGameTypes = [
      'true-false',
      'multiple-choice',
      'drag-and-drop',
      'sort-and-order',
      'fill-in-the-blanks',
      'scenario-based',
      'flashcards'
    ];
  }

  isTemplateSupported(gameType) {
    return this.supportedGameTypes.includes(gameType);
  }

  async checkTemplateAvailability(gameType, orgId, userId) {
    if (!this.isTemplateSupported(gameType) || !orgId || !userId) {
      return false;
    }

    try {
      const promptService = new PromptService(orgId, userId, '', '', '');
      
      const [questionPrompt, templateCode] = await Promise.all([
        promptService.getQuestionGenerationPrompt(gameType),
        promptService.getTemplateCode(gameType)
      ]);

        const hasDoNotUseMarker = questionPrompt && 
                                 questionPrompt.trim().toUpperCase().startsWith('(DO NOT USE)');
        
        const isAvailable = questionPrompt !== null && questionPrompt.trim() !== '' && 
                         templateCode !== null && templateCode.trim() !== '' &&
                         !hasDoNotUseMarker;
      
      return isAvailable;
    } catch (error) {
      console.warn(`Error checking template availability for ${gameType}:`, error);
      return false;
    }
  }

  async generateQuestionData(gameType, userPrompt, orgId, userId) {
    if (!this.isTemplateSupported(gameType)) {
      throw new Error(`Template not supported for game type: ${gameType}`);
    }

    try {
      let questionPrompt = null;
      let promptService = null;
      
      if (orgId && userId) {
        promptService = new PromptService(orgId, userId, '', '', '');
        questionPrompt = await promptService.getQuestionGenerationPrompt(gameType);
      }
      
      // Check if prompt is empty, null, or just whitespace
      if (!questionPrompt || !questionPrompt.trim()) {
        questionPrompt = getQuestionPrompt(gameType);
      }

      if (!questionPrompt) {
        throw new Error(`No question prompt found for game type: ${gameType}`);
      }

      let modelToUse = "claude-sonnet-4-20250514"; // fallback
      if (orgId && userId && promptService) {
        try {
          modelToUse = await promptService.getModel();
        } catch (error) {
          console.warn('Failed to get model, using fallback');
        }
      }

      const response = await anthropic.messages.create({
        messages: [
          { 
            role: "user", 
            content: questionPrompt + userPrompt 
          }
        ],
        model: modelToUse,
        max_tokens: 4096
      });

      const responseText = response.content[0].text;
      
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON found in AI response');
      }

      const questionData = JSON.parse(jsonMatch[0]);
      
      this.validateQuestionData(gameType, questionData);
      
      return questionData;
    } catch (error) {
      console.error('Error generating question data:', error);
      throw error;
    }
  }

  async refineQuestionData(gameType, originalQuestionData, userInstruction, orgId, userId) {
    if (!this.isTemplateSupported(gameType)) {
      throw new Error(`Template not supported for game type: ${gameType}`);
    }

    try {
      let refinementPromptTemplate = null;
      let promptService = null;
      
      if (orgId && userId) {
        promptService = new PromptService(orgId, userId, '', '', '');
        refinementPromptTemplate = await promptService.getRefinementPrompt();
      }
      
      // Check if refinement prompt is empty, null, or just whitespace
      if (!refinementPromptTemplate || !refinementPromptTemplate.trim()) {
        console.log('Using hardcoded refinement prompt');
        refinementPromptTemplate = questionRefinementPrompt;
      }

      const refinementPrompt = refinementPromptTemplate
        .replace('{originalData}', JSON.stringify(originalQuestionData, null, 2))
        .replace('{userInstruction}', userInstruction);

      let modelToUse = "claude-sonnet-4-20250514"; // fallback
      if (orgId && userId && promptService) {
        try {
          modelToUse = await promptService.getModel();
        } catch (error) {
          console.warn('Failed to get model, using fallback');
        }
      }

      const response = await anthropic.messages.create({
        messages: [
          { 
            role: "user", 
            content: refinementPrompt
          }
        ],
        model: modelToUse,
        max_tokens: 4096
      });

      const responseText = response.content[0].text;
      
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON found in AI response');
      }

      const refinedQuestionData = JSON.parse(jsonMatch[0]);
      
      this.validateQuestionData(gameType, refinedQuestionData);
      
      return refinedQuestionData;
    } catch (error) {
      console.error('Error refining question data:', error);
      throw error;
    }
  }

  validateQuestionData(gameType, data) {
    if (!data.title || !data.title.trim()) {
      throw new Error('Missing or empty title in question data');
    }

    switch (gameType) {
      case 'true-false':
      case 'multiple-choice':
      case 'fill-in-the-blanks':
      case 'scenario-based':
      case 'flashcards':
        if (!Array.isArray(data.questions) || data.questions.length === 0) {
          throw new Error('Missing or empty questions array');
        }
        break;
      case 'drag-and-drop':
        if (!Array.isArray(data.items) || data.items.length === 0) {
          throw new Error('Missing or empty items array');
        }
        break;
      case 'sort-and-order':
        if (!Array.isArray(data.items) || data.items.length === 0) {
          throw new Error('Missing or empty items array');
        }
        break;
      default:
        throw new Error(`Invalid game type: ${gameType}`);
    }
  }

  async generateTemplateBasedCode(gameType, userPrompt, themeColors, themeFonts, backgroundImage, orgId, userId) {
    try {
      // Step 1: Generate question data
      const questionData = await this.generateQuestionData(gameType, userPrompt, orgId, userId);
      
      // Step 2: Prepare template data
      const templateData = {
        title: questionData.title,
        questions: questionData.questions || questionData.items,
        themeColors: themeColors || "Primary Color: #402034, Secondary Color: #8d2d58, Accent Color: #F05A5B",
        themeFonts: themeFonts || "Poppins",
        backgroundImage: backgroundImage || DEFAULT_BACKGROUND_IMAGE_URL,
        ...questionData // Include any additional data like items, instructions, etc.
      };

      // Step 3: Get template code
      const generatedCode = await this.getTemplateCodeFromAdmin(gameType, templateData, orgId, userId);
      
      if (!generatedCode) {
        throw new Error(`No admin portal template found for game type: ${gameType}`);
      }

      return {
        code: generatedCode,
        questionData: questionData,
        templateData: templateData
      };
    } catch (error) {
      console.error('Error generating template-based code:', error);
      throw error;
    }
  }

  async getTemplateCodeFromAdmin(gameType, templateData, orgId, userId) {
    if (!this.isTemplateSupported(gameType)) {
      throw new Error(`Template not supported for game type: ${gameType}`);
    }

    if (orgId && userId) {
      try {
        const promptService = new PromptService(orgId, userId, '', '', '');
        const templateCode = await promptService.getTemplateCode(gameType);
        
        // Check if template code is empty, null, or just whitespace
        if (templateCode && templateCode.trim()) {
          const processedCode = this.processTemplate(templateCode, templateData);
          console.log(`Template processing successful, final code length: ${processedCode.length}`);
          return processedCode;
        } else {
          console.log(`No template code returned from admin portal for ${gameType}`);
        }
      } catch (error) {
        console.error(`Error fetching or processing template from admin for ${gameType}:`, error);
        throw error;
      }
    } else {
      console.log(`No orgId (${orgId}) or userId (${userId}) provided for template fetch`);
    }
    
    console.log(`No admin portal template found for ${gameType}, will use prompt-based generation`);
    return null;
  }

  processTemplate(templateCode, templateData) {
    try {
      console.log('Processing template with data:', { 
        title: templateData.title, 
        themeColors: templateData.themeColors,
        themeFonts: templateData.themeFonts,
        backgroundImage: templateData.backgroundImage,
        questionsCount: templateData.questions?.length 
      });
      
      let processedCode = templateCode;
      
      processedCode = processedCode.replace(/\\`/g, '`'); // Fix escaped backticks
      processedCode = processedCode.replace(/\\\$/g, '$'); // Fix escaped dollar signs
      processedCode = processedCode.replace(/\\n/g, '\n'); // Fix escaped newlines
      processedCode = processedCode.replace(/\\t/g, '\t'); // Fix escaped tabs
      processedCode = processedCode.replace(/\\"/g, '"'); // Fix escaped quotes
      processedCode = processedCode.replace(/\\'/g, "'"); // Fix escaped single quotes
      processedCode = processedCode.replace(/\\\\/g, '\\'); // Fix double escaped backslashes
      
      // Replace common placeholders
      processedCode = processedCode.replace(/\$\{title\}/g, templateData.title || '');
      processedCode = processedCode.replace(/\$\{themeFonts\}/g, templateData.themeFonts || 'Poppins');
      processedCode = processedCode.replace(/\$\{backgroundImage\}/g, templateData.backgroundImage || DEFAULT_BACKGROUND_IMAGE_URL);
      
      // Replace questions array - handle both placeholder patterns
      if (templateData.questions) {
        const questionsJson = JSON.stringify(templateData.questions, null, 2);
        processedCode = processedCode.replace(/\$\{JSON\.stringify\(questions, null, 2\)\}/g, questionsJson);
        // handle if it's just ${questions}
        processedCode = processedCode.replace(/\$\{questions\}/g, questionsJson);
      }
      
      // Replace items array for drag-and-drop and sort-and-order templates
      if (templateData.items) {
        const itemsJson = JSON.stringify(templateData.items, null, 2);
        processedCode = processedCode.replace(/\$\{JSON\.stringify\(items, null, 2\)\}/g, itemsJson);
        processedCode = processedCode.replace(/\$\{JSON\.stringify\(items\)\}/g, JSON.stringify(templateData.items));
        processedCode = processedCode.replace(/\$\{items\}/g, itemsJson);
      }
      
      // Replace theme colors
      if (templateData.themeColors) {
        const colors = parseThemeColors(templateData.themeColors);
        
        processedCode = processedCode.replace(/\$\{colors\.primary\}/g, colors.primary);
        processedCode = processedCode.replace(/\$\{colors\.secondary\}/g, colors.secondary);
        processedCode = processedCode.replace(/\$\{colors\.accent\}/g, colors.accent);
      }
      
      // Check if there are any remaining unprocessed placeholders
      const remainingPlaceholders = processedCode.match(/\$\{[^}]+\}/g);
      if (remainingPlaceholders) {
        console.warn('Found unprocessed placeholders in template:', remainingPlaceholders);
      }
      
      console.log('Template processing completed successfully');
      return processedCode;
    } catch (error) {
      console.error('Error processing template:', error);
      console.error('Template data:', templateData);
      console.error('Template code length:', templateCode?.length);
      console.error('Template code sample (first 500 chars):', templateCode?.substring(0, 500));
      throw new Error(`Failed to process template: ${error.message}`);
    }
  }

  getTemplateCode(gameType, templateData) {
    if (!this.isTemplateSupported(gameType)) {
      throw new Error(`Template not supported for game type: ${gameType}`);
    }

    const generatedCode = getGameTemplate(gameType, templateData);
    
    if (!generatedCode) {
      throw new Error(`No template found for game type: ${gameType}`);
    }

    return generatedCode;
  }

  getSupportedGameTypes() {
    return [...this.supportedGameTypes];
  }
}

export const templateManager = new TemplateManager(); 